import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { BookButton, SectionHeading } from "@/components/ui-bits";
import NotFound from "@/pages/NotFound";
import { AUDIENCES, HOTEL } from "@/lib/site";

export default function AudiencePage({ slug }) {
  const a = AUDIENCES[slug];
  if (!a) return <NotFound />;

  return (
    <>
      <Seo
        title={a.seoTitle}
        description={a.seoDescription}
        path={`/${slug}`}
        image={a.hero}
        schema={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: a.eyebrow, path: `/${slug}` }])]}
      />
      <PageHero
        eyebrow={a.eyebrow}
        title={a.title}
        subtitle={a.intro}
        image={a.hero}
        crumbs={[{ name: "Home", path: "/" }, { name: a.eyebrow.replace("For ", ""), path: `/${slug}` }]}
      />

      <section className="bg-bone py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal><SectionHeading eyebrow="Why Guests Choose Us" title="Thoughtful comforts for your trip." /></Reveal>
          <Stagger className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {a.benefits.map((b) => {
              const Icon = Icons[b.icon] || Icons.Check;
              return (
                <StaggerItem key={b.title}>
                  <div className="border-t border-stone/20 pt-6">
                    <Icon className="text-amber" size={28} strokeWidth={1.3} />
                    <h3 className="font-serif text-2xl text-forest mt-4">{b.title}</h3>
                    <p className="text-stone mt-2 leading-relaxed text-[15px]">{b.copy}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <section className="bg-sand py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal><SectionHeading eyebrow="We Welcome" title="Perfect for every kind of trip." /></Reveal>
          <Stagger className="mt-12 flex flex-wrap gap-3">
            {a.audiences.map((x) => (
              <StaggerItem key={x}>
                <span className="inline-block rounded-full border border-forest/20 bg-bone px-5 py-2.5 text-sm text-forest">
                  {x}
                </span>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-forest text-bone py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl">Let's plan your stay.</h2>
            <p className="mt-5 text-bone/75 leading-relaxed">
              Book direct for the best available rate, or call our 24-hour front desk to talk through your needs.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <BookButton label="Book Direct & Save" testid="audience-book" />
              <a href={HOTEL.phoneHref} data-testid="audience-call" className="inline-flex items-center rounded-full border border-bone/40 text-bone px-8 py-4 text-sm font-medium hover:bg-amber hover:border-amber transition-colors">
                Call {HOTEL.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
